import { useToast } from '@common/hooks';
import { cn } from '@common/utils';
import { NodeSelection } from 'prosemirror-state';
import React, { useContext, useState } from 'react';

import { CustomEditorContext } from '.';
import BoxedTextIcon from './_assets/boxed-text.svg';
import HrIcon from './_assets/hr.svg';
import type { ImageButtonProps } from './_components/ImageButton';
import { ImageButton } from './_components/ImageButton';
import { ImageEditButton } from './_components/ImageEditButton';
import { LinkButton } from './_components/LinkButton';
import { PreviewButton } from './_components/PreviewButton';
import { YouTubeButton } from './_components/YouTubeButton';
import { TEXT_ALIGN } from './_constants';
import type { CustomImageOptions } from './_utils/CustomImage';
import type { CustomLinkOptions } from './_utils/CustomLink';
import { extractAlignValue } from './_utils/extractAlignValue';
import { extractMaxWidthValue } from './_utils/extractMaxWidthValue';
import type { BizFontSizeType } from './_utils/FontSize';
import { BIZ_FONT_SIZES, DEFAULT_FONT_SIZE } from './_utils/FontSize';
import { FONT_WEIGHTS } from './_utils/FontWeight';
import { getYouTubeVideoId } from './_utils/getYouTubeVideoId';
import { HIGHLIGHTS } from './_utils/Highlight';
import { TEXT_COLORS } from './_utils/TextColor';

interface ToolbarProps {
  imageUploadOptions?: ImageButtonProps;
}

const DEFAULT_CUSTOM_IMAGE_OPTIONS = {
  maxWidth: null,
  isBorderRadius: false,
  align: 'left',
} as const;

const DEFAULT_CUSTOM_LINK_OPTIONS = {
  theme: 'text',
  size: 'H56',
  isOutLink: false,
  href: '',
} as const;

export const BizToolbar = ({ imageUploadOptions }: ToolbarProps) => {
  const { toast, toastError } = useToast();
  const { editor } = useContext(CustomEditorContext);

  const isImageSelected =
    editor?.state.selection instanceof NodeSelection &&
    editor?.state.selection.node.type.name === 'image';

  const [customImageOptions, setCustomImageOptions] =
    useState<CustomImageOptions>(DEFAULT_CUSTOM_IMAGE_OPTIONS);
  const [customLinkOptions, setCustomLinkOptions] = useState<CustomLinkOptions>(
    DEFAULT_CUSTOM_LINK_OPTIONS,
  );
  const [youTubeUrl, setYouTubeUrl] = useState('');

  /** 이미지 편집 모달을 띄울 때 선택된 태그에 적용된 값을 파싱해서 불러옴 */
  const initialImageOptions = () => {
    const styleText =
      (editor?.state as any)?.selection?.node?.attrs?.style ?? '';
    const classText =
      (editor?.state as any)?.selection?.node?.attrs?.class ?? '';

    const maxWidth = extractMaxWidthValue(styleText);
    const isBorderRadius = classText.includes('rounded');
    const align = extractAlignValue(classText);

    setCustomImageOptions({
      maxWidth,
      isBorderRadius,
      align,
    });
  };

  const initialLinkOptions = () => {
    if (isImageSelected) {
      setCustomLinkOptions((prev) => ({
        ...prev,
        theme: 'image',
      }));
    }
  };

  if (!editor) {
    return null;
  }

  const [selectedColor] =
    TEXT_COLORS.find(([, color]) => editor.isActive('textStyle', { color })) ??
    [];

  const selectedFontWeight = FONT_WEIGHTS.find((fontWeight) =>
    editor.isActive('fontWeight', { fontWeight }),
  );

  const selectedFontSize =
    BIZ_FONT_SIZES.find((fontSize) => {
      return editor.isActive('fontSize', { fontSize });
    }) ?? DEFAULT_FONT_SIZE;

  const isBoxedText = editor.isActive('boxedText');

  const selectedTextAlign = TEXT_ALIGN.find((textAlign) =>
    editor.isActive('textAlign', { textAlign }),
  );

  return (
    <nav className="flex flex-wrap items-center justify-center gap-8 bg-Gray-100 p-8">
      <select
        value={selectedFontSize}
        onChange={(e) => {
          editor
            .chain()
            .focus()
            .setBizFontSize(e.target.value as BizFontSizeType)
            .run();
        }}
      >
        {BIZ_FONT_SIZES.map((fontSize) => (
          <option key={fontSize} className={fontSize} value={fontSize}>
            {fontSize}
          </option>
        ))}
      </select>
      <select
        value={selectedTextAlign}
        onChange={(e) => {
          editor?.chain().focus().setTextAlign(e.target.value).run();
        }}
      >
        {TEXT_ALIGN.map((textAlign) => (
          <option key={textAlign} value={textAlign}>
            {textAlign}
          </option>
        ))}
      </select>

      <section className="flex gap-8 rounded-small border-1 border-Gray-600 px-8 py-2">
        {TEXT_COLORS.map(([label, color]) => (
          <button
            key={label}
            className={cn('font-800', {
              underline: selectedColor === label,
            })}
            style={{
              color,
            }}
            type="button"
            onClick={() => editor?.chain().focus().setColor(color).run()}
          >
            A
          </button>
        ))}
      </section>
      <section className="flex gap-8 rounded-small border-1 border-Gray-600 px-8 py-2">
        {FONT_WEIGHTS.map((fontWeight) => (
          <button
            key={fontWeight}
            className={cn(fontWeight, {
              underline: selectedFontWeight === fontWeight,
            })}
            type="button"
            onClick={() =>
              editor?.chain().focus().setFontWeight(fontWeight).run()
            }
          >
            B
          </button>
        ))}
      </section>
      {HIGHLIGHTS.map((highlight) => {
        const isActive = editor.isActive('highlight', { highlight });

        return (
          <button
            key={highlight}
            className={cn(
              highlight,
              'flex size-24 min-h-24 min-w-24 items-center justify-center rounded-small font-700',
              {
                'border-1 border-Gray-800': isActive,
              },
            )}
            type="button"
            onClick={() =>
              isActive
                ? editor.chain().focus().unsetHighlight().run()
                : editor.chain().focus().setHighlight(highlight).run()
            }
          />
        );
      })}
      <button
        className={cn('box-border flex size-24 items-center justify-center', {
          'border-Gray-800 border-1 rounded-xsmall': isBoxedText,
        })}
        type="button"
        onClick={() =>
          isBoxedText
            ? editor.chain().focus().unsetBoxedText().run()
            : editor.chain().focus().setBoxedText().run()
        }
      >
        <BoxedTextIcon />
      </button>
      <button
        type="button"
        onClick={() => {
          editor?.chain().focus().setHorizontalRule().run();
        }}
      >
        <HrIcon />
      </button>
      <ImageButton {...imageUploadOptions} />
      <ImageEditButton
        disabled={!isImageSelected}
        selectedOptions={customImageOptions}
        onAlignChange={(align) => {
          setCustomImageOptions((prev) => ({
            ...prev,
            align,
          }));
        }}
        onClick={initialImageOptions}
        onIsBorderRadiusChange={(isBorderRadius) => {
          setCustomImageOptions((prev) => ({
            ...prev,
            isBorderRadius,
          }));
        }}
        onSubmit={() => {
          editor.chain().focus().setCustomImage(customImageOptions).run();
          setCustomImageOptions(DEFAULT_CUSTOM_IMAGE_OPTIONS); //* 에디터 반영후 초기화
        }}
        onWidthChange={(maxWidth) => {
          setCustomImageOptions((prev) => ({
            ...prev,
            maxWidth,
          }));
        }}
      />
      <YouTubeButton
        youTubeUrl={youTubeUrl}
        onSubmit={() => {
          if (youTubeUrl) {
            try {
              const videoId = getYouTubeVideoId(youTubeUrl);

              editor
                .chain()
                .focus()
                .setCustomYouTubeIframe({
                  src: `https://www.youtube.com/embed/${videoId}`,
                })
                .run();

              setYouTubeUrl(''); //* 에디터 반영후 초기화
            } catch (error: any) {
              if (error?.message) {
                toast({
                  type: 'fail',
                  message: error.message,
                });

                return;
              }

              toastError({ error });
            }
          }
        }}
        onYouTubeUrlChange={setYouTubeUrl}
      />
      <LinkButton
        selectedOptions={customLinkOptions}
        onClick={initialLinkOptions}
        onHrefChange={(href) => {
          setCustomLinkOptions((prev) => ({
            ...prev,
            href,
          }));
        }}
        onIsOutLinkChange={(isOutLink) => {
          setCustomLinkOptions((prev) => ({
            ...prev,
            isOutLink,
          }));
        }}
        onSizeChange={(size) => {
          setCustomLinkOptions((prev) => ({
            ...prev,
            size,
          }));
        }}
        onSubmit={() => {
          editor.chain().focus().setCustomLink(customLinkOptions).run();
          setCustomLinkOptions(DEFAULT_CUSTOM_LINK_OPTIONS); //* 에디터 반영후 초기화
        }}
        onThemeChange={(theme) => {
          setCustomLinkOptions((prev) => ({
            ...prev,
            theme,
          }));
        }}
      />
      <PreviewButton />
    </nav>
  );
};
